'use client';

import { useState, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'lib/hooks';
import { removeFromComparison, markForRemoval } from 'slices/comparisonSlice';
import {
  selectAlexRecommendedPlan,
  selectComparisonPlans,
  selectComparisonRemovingIds,
} from 'slices/selectors';
import { useRouter } from 'next/navigation';
import { findPlanType } from 'utils/planDetails';
import { isSamePlan, planIdentityKey } from 'utils/selectedPlans';
import { Location } from 'slices/locationSlice';
import XCloseIcon from 'components/icons/XCloseIcon';
import CaretIcon from 'components/icons/CaretIcon';
import AlexRecommendedBadge from './AlexRecommendedBadge';
import Loading from 'components/Loading';
import UIButton from 'components/buttons/UIButton';
import styles from './ComparisonTray.module.scss';

interface ComparisonTrayProps {
  planType: string;
  year: string;
  userId?: string;
  location?: Location;
}

const ComparisonTray = ({
  planType,
  year,
  userId,
  location,
}: ComparisonTrayProps) => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const plans = useAppSelector((state) =>
    selectComparisonPlans(state, planType, year)
  );
  const removingIds = useAppSelector(selectComparisonRemovingIds);
  const recommendedPlans = useAppSelector(selectAlexRecommendedPlan);
  const scheduledRef = useRef<Set<string>>(new Set());

  const [isOpen, setIsOpen] = useState(true);
  const [isNavigating, setIsNavigating] = useState(false);
  const autoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const numPlans = plans.filter(
    (plan) => !removingIds.includes(planIdentityKey(plan))
  ).length;

  const hasRecommendedPlan = plans.some(
    (plan) => recommendedPlans?.some((r) => isSamePlan(r, plan)) ?? false
  );

  const [prevNumPlans, setPrevNumPlans] = useState(numPlans);
  if (numPlans !== prevNumPlans) {
    setPrevNumPlans(numPlans);
    if (numPlans >= 1) setIsOpen(true);
  }

  useEffect(() => {
    removingIds.forEach((identityKey) => {
      if (scheduledRef.current.has(identityKey)) return;
      scheduledRef.current.add(identityKey);
      const plan = plans.find((p) => planIdentityKey(p) === identityKey);
      setTimeout(() => {
        if (plan) {
          dispatch(removeFromComparison({ plan, planType }));
        }
        scheduledRef.current.delete(identityKey);
      }, 400);
    });
  }, [removingIds, planType, dispatch, plans]);

  // If tray contains 1 plan, close after 2 sec; cancel timer when numPlans changes
  useEffect(() => {
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }
    if (numPlans !== 1) return;
    autoCloseTimerRef.current = setTimeout(() => {
      setIsOpen(false);
      autoCloseTimerRef.current = null;
    }, 2000);
    return () => {
      if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
    };
  }, [numPlans]);

  if (plans.length === 0) return null;

  if (isNavigating) {
    return (
      <div className={styles.navigatingOverlay}>
        <Loading message={'Looking up plan details...'} />
      </div>
    );
  }

  const handleAccordion = () => {
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }
    setIsOpen((prev) => !prev);
  };

  const handleRemove = (plan: (typeof plans)[number]) => {
    dispatch(markForRemoval(plan));
  };

  const handleCompare = () => {
    const activePlans = plans.filter(
      (p) => !removingIds.includes(planIdentityKey(p))
    );
    const [plan1, plan2] = activePlans;
    const params = new URLSearchParams({
      planId1: String(plan1.id),
      planId2: String(plan2.id),
      year1: String(plan1.planYear ?? year),
      year2: String(plan2.planYear ?? year),
    });
    if (userId) params.set('userId', userId);
    if (location?.zipcode) params.set('zip', location.zipcode);
    if (location?.countyFips) params.set('county', location.countyFips);
    if (location?.state) params.set('state', location.state);
    setIsNavigating(true);
    router.push(`/browse/compare/${year}/${planType}?${params.toString()}`);
  };

  return (
    <div
      className={`${styles.tray} ${!isOpen ? styles.trayClosed : ''} ${
        hasRecommendedPlan ? styles.trayWithBadge : ''
      }`}
      role="region"
      aria-label="Plan comparison"
    >
      <div className={styles.trayContent}>
        <div className={styles.title}>
          <h4>
            Compare {year} {findPlanType(planType)}s ({numPlans} of 2)
          </h4>
          <button
            className={styles.accordionBtn}
            onClick={handleAccordion}
            aria-expanded={isOpen}
          >
            <CaretIcon
              direction={isOpen ? 'down' : 'up'}
              color="darkPurple"
              size="large"
            />
          </button>
        </div>

        <div
          className={`${styles.plansContainer} ${
            !isOpen ? styles.rowCollapsed : ''
          }`}
        >
          <div
            className={`${styles.planSlots} ${
              !hasRecommendedPlan ? styles.planSlotsNoBadge : ''
            }`}
          >
            {[0, 1].map((i) => {
              const plan = plans[i];
              const isRemoving = plan
                ? removingIds.includes(planIdentityKey(plan))
                : false;
              const isRecommended = plan
                ? recommendedPlans?.some((r) => isSamePlan(r, plan)) ?? false
                : false;
              return plan ? (
                <div
                  key={planIdentityKey(plan)}
                  className={`${styles.selectedPlan} ${
                    isRemoving ? styles.planRemoving : ''
                  }`}
                >
                  {isRecommended && (
                    <AlexRecommendedBadge
                      className={styles.trayBadge}
                      starSize="small"
                    />
                  )}
                  <div className={styles.planContent}>
                    <p className={styles.planName}>{plan.name}</p>
                    <button
                      className={styles.removeBtn}
                      onClick={() => handleRemove(plan)}
                      disabled={isRemoving}
                      aria-label={`Remove ${plan.name} from comparison`}
                    >
                      <XCloseIcon />
                    </button>
                  </div>
                </div>
              ) : (
                <div key={i} className={styles.emptyPlanSlot} />
              );
            })}
          </div>

          <UIButton
            buttonType="secondary"
            label="Compare plans"
            disabled={numPlans < 2}
            onClick={handleCompare}
          />
        </div>
      </div>
    </div>
  );
};

export default ComparisonTray;
